import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Loader2, ScanLine, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/i18n";
import { PasswordInput } from "@/components/PasswordInput";
import { CognitoError } from "@/types/auth";
import { BookbaseLogo } from "@/components/BookbaseLogo";

const FEATURES = [
  { icon: ScanLine, titleKey: "auth.feature.scan", titleFallback: "Scannen statt Suchen", bodyKey: "auth.feature.scanBody", bodyFallback: "Ausleihe und Rückgabe per Barcode, in Sekunden erledigt." },
  { icon: BookOpen, titleKey: "auth.feature.catalog", titleFallback: "Ein Bestand, zwei Bibliotheken", bodyKey: "auth.feature.catalogBody", bodyFallback: "Bücherfonds und Bibliothek zentral verwalten, jederzeit den Überblick behalten." },
  { icon: Users, titleKey: "auth.feature.portal", titleFallback: "Eigener Zugang für alle", bodyKey: "auth.feature.portalBody", bodyFallback: "Schüler und Lehrer sehen ihre Ausleihen selbst, ohne Rückfrage am Schalter." },
];

export function Login() {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const outcome = await signIn(username, password);
      if (outcome === "new-password-required") navigate("/login/new-password");
      else navigate("/", { replace: true });
    } catch (err) {
      const code = err instanceof CognitoError ? err.code : "generic";
      setError(t(`auth.error.${code}`, t("auth.error.generic", "Anmeldung fehlgeschlagen. Bitte erneut versuchen.")));
      // Only clear busy on failure: a successful sign-in navigates away immediately
      // (see try block above), so keeping the button in its loading state until
      // then avoids a flash of an enabled button right before the page changes.
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Brand panel: hidden on small screens so the form is the only thing a phone has
          to scroll past -- this app's staff sign-in is normally used on a desktop/tablet
          at a circulation desk, but a librarian locked out of the console shouldn't be
          blocked from signing in on whatever device is at hand. */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-brand-ink p-12 text-white lg:flex">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-brand-green/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-brand-green/10 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <BookbaseLogo size={36} className="rounded-lg" alt={t("app.name", "Bookbase")} />
          <span className="text-lg font-semibold">{t("app.name", "Bookbase")}</span>
        </div>
        <div className="relative space-y-10">
          <h1 className="max-w-md text-3xl font-semibold leading-tight">
            {t("auth.tagline", "Die Schulbibliothek, digital im Griff.")}
          </h1>
          <ul className="space-y-6">
            {FEATURES.map(({ icon: Icon, titleKey, titleFallback, bodyKey, bodyFallback }) => (
              <li key={titleKey} className="flex gap-4">
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-brand-green text-brand-ink">
                  <Icon size={20} />
                </span>
                <div>
                  <p className="font-medium">{t(titleKey, titleFallback)}</p>
                  <p className="mt-0.5 text-sm text-white/60">{t(bodyKey, bodyFallback)}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-white/40">{t("app.name", "Bookbase")}</p>
      </div>

      {/* Sign-in form */}
      <div className="flex w-full flex-1 items-center justify-center px-4 py-12 lg:w-1/2">
        <form onSubmit={(event) => void submit(event)} className="w-full max-w-sm space-y-5">
          <div className="flex items-center gap-3 lg:hidden">
            <BookbaseLogo size={40} className="rounded-xl" alt={t("app.name", "Bookbase")} />
            <h1 className="text-xl font-semibold text-gray-900">{t("app.name", "Bookbase")}</h1>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-gray-900">{t("auth.loginTitle", "Anmelden")}</h2>
            <p className="mt-1 text-sm text-gray-500">{t("auth.loginSubtitle", "Mit deinem Bookbase-Konto anmelden.")}</p>
          </div>

          {error && (
            <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error}
            </p>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="username">
              {t("auth.username", "Benutzername")}
            </label>
            <input
              id="username"
              className="tap-target w-full rounded-lg border border-gray-300 px-3 text-base focus:border-brand-primary focus:outline-2 focus:outline-offset-1 focus:outline-brand-primary"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              disabled={busy}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="password">
              {t("auth.password", "Passwort")}
            </label>
            <PasswordInput
              id="password"
              className="tap-target w-full rounded-lg border border-gray-300 px-3 text-base focus:border-brand-primary focus:outline-2 focus:outline-offset-1 focus:outline-brand-primary"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              disabled={busy}
              required
            />
          </div>

          <button
            type="submit"
            disabled={busy}
            aria-busy={busy}
            className="tap-target flex w-full items-center justify-center gap-2 rounded-lg bg-brand-green py-3 text-sm font-medium text-brand-ink transition-opacity disabled:opacity-70"
          >
            {busy && <Loader2 size={18} className="animate-spin" aria-hidden />}
            {busy ? t("auth.submitting", "Anmeldung läuft …") : t("auth.submit", "Anmelden")}
          </button>
        </form>
      </div>
    </div>
  );
}
